// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Container } from './container';
import { Component } from './component';
import { Tooltip } from './tooltip';
import { escapeAttribute } from './common';

/**
 * @class Jsw.ToolList
 * @extends Jsw.Container
 *
 * Example usage:
 *
 *     @example
 *     new Jsw.ToolList({
 *         renderTo: document.body,
 *         items: [{
 *             componentType: 'Jsw.toollist.Panel',
 *             title: 'Components',
 *             image: 'class-icons/component-large.png',
 *             items: [{
 *                 componentType: 'Jsw.toollist.Item',
 *                 title: 'Jsw.Box',
 *                 href: './#!/api/Jsw.Box',
 *                 target: '_parent'
 *             }, {
 *                 componentType: 'Jsw.toollist.Item',
 *                 title: 'Jsw.Button',
 *                 href: './#!/api/Jsw.Button',
 *                 target: '_parent',
 *                 attention: true
 *             }]
 *         }, {
 *             componentType: 'Jsw.toollist.Panel',
 *             title: 'Classes',
 *             image: 'class-icons/class-large.png',
 *             items: [{
 *                 componentType: 'Jsw.toollist.Item',
 *                 title: 'Jsw.Locale',
 *                 href: './#!/api/Jsw.Locale',
 *                 target: '_parent'
 *             }]
 *         }, {
 *             componentType: 'Jsw.toollist.Panel',
 *             title: 'Singletons',
 *             image: 'class-icons/singleton-large.png',
 *             items: [{
 *                 componentType: 'Jsw.toollist.Item',
 *                 title: 'Jsw.Cookie',
 *                 href: './#!/api/Jsw.Cookie',
 *                 target: '_parent'
 *             }]
 *         }]
 *     });
 */
export const ToolList = Class.create(Container, {
    _initConfiguration($super, config) {
        $super(config);
        this._tools = this._getConfigParam('tools', null);
        this._cls = this._getConfigParam('cls', 'tools-list-box');
        this._contentAreaId = `${this._id}-tools-list`;

        if (Object.isArray(this._tools) && (0 === this._operations.size())) {
            this._operations = null;
        }
    },

    _initComponentElement($super) {
        $super();
        this._updateComponentElement(
            `<div id="${this._id}-box-area" class="box-area">` +
                `<div id="${this._id}-content" class="content">` +
                        `<div id="${this._id}-content-area" class="content-area">` +
                            `<ul id="${this._id}-tools-list" class="tools-list"></ul>` +
                        '</div>' +
                '</div>' +
            '</div>'
        );
    },

    render($super) {
        $super();
        Tooltip.initData(this._componentElement);
    },
});

export const toollist = {};
/**
 * @class Jsw.toollist.Panel
 * @extends Jsw.Container
 */
toollist.Panel = Class.create(Container, {
    _initConfiguration($super, config) {
        $super(config);

        this._contentAreaId = `${this._id}-tools-list`;
        this._image = this._getConfigParam('image', '');
        this._title = this._getConfigParam('title', '');
    },

    _initComponentElement($super) {
        const attributes = { class: 'tools-item' };
        const forcedId = this._getConfigParam('id', null);
        if (forcedId) {
            attributes.id = forcedId;
        }

        this._componentElement = new Element('li', attributes);
        this._componentElement.update(new Element('div', {
            class: 'tool-block',
        })
            .insert(
                '<span class="tool-icon">' +
                    `<img src="${this._image}" />` +
                '</span>' +
                `<span class="tool-name">${this._title}</span>`
            )
            .insert(new Element('ul', {
                id: `${this._id}-tools-list`,
                class: 'tool-info' })
            ));
    },
});

/**
 * @class Jsw.toollist.Item
 * @extends Jsw.Component
 */
toollist.Item = Class.create(Component, {
    _initConfiguration($super, config) {
        $super(config);

        this._handler = this._getConfigParam('handler', false);
        if (Object.isString(this._handler)) {
            // eslint-disable-next-line no-eval
            eval(`this._handler = ${this._handler}`);
        }
        this._href = this._getConfigParam('href', '#');
        this._target = this._getConfigParam('target', null);
        this._title = this._getConfigParam('title', '');
        this._disabled = this._getConfigParam('disabled', false);
        this._attention = this._getConfigParam('attention', false);
    },

    _initComponentElement($super) {
        const href = this._disabled ? '#' : this._href;

        let title = this._title.escapeHTML();
        if (this._attention) {
            title = new Element('span', {
                class: 'hint-attention',
            }).update(title);
        }

        this._componentElement = new Element('li', {
            id: this._getConfigParam('id', null),
        })
            .update(new Element('a', {
                href,
                target: this._target,
            }).update(title));

        if (this._handler && !this._disabled) {
            const handler = this._handler;
            this._componentElement.down('a').observe('click', function (e) {
                e.preventDefault();
                handler.call(this, e);
            });
        }
    },

    _addTooltips() {
        const description = this._getConfigParam('description');
        if (description) {
            this._tooltip = Tooltip.init(this._componentElement.down('a'), { text: description });
        }
    },
});

/**
 * @class Jsw.toollist.Button
 * @extends Jsw.Component
 */
toollist.Button = Class.create(Component, {
    _initConfiguration($super, config) {
        $super(config);

        this._handler = this._getConfigParam('handler', false);
        if (Object.isString(this._handler)) {
            // eslint-disable-next-line no-eval
            eval(`this._handler = ${this._handler}`);
        }
        this._href = this._getConfigParam('href', '#');
        this._target = this._getConfigParam('target', null);
        this._image = this._getConfigParam('image', '');
        this._disabledImage = this._getConfigParam('disabledImage', this._image);
        this._title = this._getConfigParam('title', '');
        this._description = this._getConfigParam('description', '');
        this._disabled = this._getConfigParam('disabled', false);
    },

    _initComponentElement() {
        const attributes = { class: 'tools-item' };
        const forcedId = this._getConfigParam('id', null);
        if (forcedId) {
            attributes.id = forcedId;
        }

        this._componentElement = new Element('li', attributes);

        let href;
        let image;
        let cls = 'tool-block';

        if (this._disabled) {
            cls += ' tool-block-disabled';
            href = '#';
            image = this.getImagePath(this._disabledImage);
        } else {
            href = this._href;
            image = this.getImagePath(this._image);
        }
        this._componentElement.update(
            new Element('a', {
                class: cls,
                href,
                target: this._target,
            })
                .insert(
                    '<span class="tool-icon">' +
                        `<img src="${escapeAttribute(image)}"/>` +
                    '</span>' +
                    `<span class="tool-name">${this._title.escapeHTML()}</span>` +
                    `<span class="tool-info">${this._description.escapeHTML()}</span>`
                )
        );

        if (this._handler && !this._disabled) {
            this._componentElement.observe('click', this._handler);
        }
    },

    _addTooltips() {
        // don't show tooltips, 'cause we've inline descriptions
    },

    getImagePath(path) {
        return `${Jsw.skinUrl}/${path}`;
    },
});



// WEBPACK FOOTER //
// ./jsw/tool-list.js